import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Info, Coins } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { GoldButton, GhostButton } from "./common";
import { CompatibilityInline } from "./AstrologyCompatibility";

const ACTIVITIES = ["Dinner", "Cocktails", "Art Gallery", "Private Concert", "Coffee", "Wine Tasting"];
const PRESETS = [100, 200, 350, 500];

function Overlay({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-md db-glass rounded-t-3xl sm:rounded-3xl border border-[#D4AF37]/25 max-h-[92vh] overflow-y-auto db-fade-up" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] text-[#C9A96E]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const inputCls = "w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-4 py-3 text-[#FBF8F3] placeholder:text-[#9E978E]";

export function MakeOfferModal({ recipient, compatibility, onClose, onDone }) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(recipient.preferred_offer || 150);
  const [message, setMessage] = useState("");
  const [activity, setActivity] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [fee, setFee] = useState(null);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    api.get("/credits/catalog").then((r) => setFee(r.data.feature_costs?.offer_submission ?? 0)).catch(() => setFee(0));
    api.get("/credits/balance").then((r) => setBalance(r.data.credits)).catch(() => {});
  }, []);

  const insufficient = fee != null && balance != null && balance < fee;

  const submit = async () => {
    if (!amount || amount < 1) return toast.error("Enter a valid offer amount");
    if (insufficient) { toast.error("Not enough DateBid credits to submit this offer."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/offers", {
        recipient_id: recipient.user_id, amount: Number(amount), message: message || null,
        activity: activity || null, preferred_date: date || null, preferred_time: time || null,
      });
      const spent = data?.credits_spent || 0;
      toast.success("Date offer sent", { description: spent ? `Platform fee: ${spent} credits. Your $${amount} offer is on its way.` : `Your $${amount} offer is on its way.` });
      onDone && onDone();
      onClose();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "Could not send offer";
      if (status === 402) {
        toast.error(detail, { description: "Buy or top up DateBid credits to continue." });
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">Make Your Move</div>
            <h3 className="font-serif text-2xl text-[#FBF8F3] mt-1">Place a Date Offer</h3>
            <p className="text-sm text-[#9E978E] mt-1">for {recipient.display_name}</p>
          </div>
          <button onClick={onClose} className="text-[#9E978E] hover:text-[#FBF8F3]"><X size={22} /></button>
        </div>

        <div className="db-gold-rule my-5" />

        {compatibility && <div className="mb-4"><CompatibilityInline data={compatibility} /></div>}

        <div className="space-y-4">
          <Field label="Date Offer Amount (USD)">
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESETS.map((p) => (
                <button key={p} data-testid={`offer-preset-${p}`} onClick={() => setAmount(p)}
                  className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${Number(amount) === p ? "bg-[#541224] border-[#D4AF37]/60 text-[#FBF8F3]" : "border-[#D4AF37]/20 text-[#D8D2C9] hover:border-[#D4AF37]/50"}`}>
                  ${p}
                </button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-serif text-lg">$</span>
              <input data-testid="make-offer-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls + " pl-8 font-serif text-lg"} />
            </div>
          </Field>

          <Field label="Proposed Activity (optional)">
            <div className="flex flex-wrap gap-2">
              {ACTIVITIES.map((a) => (
                <button key={a} onClick={() => setActivity(activity === a ? "" : a)}
                  className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${activity === a ? "bg-[#541224] border-[#D4AF37]/60 text-[#FBF8F3]" : "border-[#D4AF37]/20 text-[#D8D2C9] hover:border-[#D4AF37]/50"}`}>
                  {a}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Preferred Date"><input data-testid="make-offer-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></Field>
            <Field label="Preferred Time"><input data-testid="make-offer-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} /></Field>
          </div>

          <Field label="A Thoughtful Note (optional)">
            <textarea data-testid="make-offer-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="I'd love to meet you over dinner and hear your story…" className={inputCls + " resize-none"} />
          </Field>

          {/* Platform fee + no-escrow disclosure */}
          {fee > 0 && (
            <div data-testid="offer-fee-box" className={`rounded-xl border p-3 ${insufficient ? "bg-[#8B263E]/15 border-[#8B263E]/40" : "bg-[#0C0A0A]/50 border-[#D4AF37]/15"}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-[#D8D2C9]"><Coins size={14} className="text-[#D4AF37]" /> Platform fee to submit</span>
                <span className="font-serif text-[#D4AF37]" data-testid="offer-fee-amount">{fee} credits</span>
              </div>
              {balance != null && (
                <div className="text-[11px] text-[#9E978E] mt-1" data-testid="offer-fee-balance">
                  {insufficient
                    ? `You have ${balance} credits — you need ${fee - balance} more.`
                    : `Balance ${balance} → ${balance - fee} after submitting.`}
                </div>
              )}
              {insufficient && (
                <button data-testid="offer-buy-credits-btn" onClick={() => navigate("/wallet")} className="mt-2 text-xs rounded-full px-3 py-1 border border-[#D4AF37]/50 text-[#D4AF37] hover:border-[#D4AF37]">Buy / Top Up Credits</button>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 rounded-xl bg-[#0C0A0A]/50 border border-[#D4AF37]/15 p-3">
            <Info size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
            <p className="text-xs text-[#9E978E]">The ${amount || 0} is a <strong className="text-[#D8D2C9]">direct arrangement between you and {recipient.display_name}</strong>. DateBid never collects, holds, or guarantees this amount — you bring it to the date yourselves. The credit fee above is a separate platform cost. Always meet in public.</p>
          </div>

          <div className="flex gap-3 pt-1">
            <GhostButton onClick={onClose} className="flex-1">Cancel</GhostButton>
            <GoldButton data-testid="make-offer-submit-btn" onClick={submit} disabled={busy || insufficient} className="flex-1">{busy ? "Sending…" : (fee > 0 ? `Place Offer · ${fee} credits` : "Place Offer")}</GoldButton>
          </div>
        </div>
      </div>
    </Overlay>
  );
}

export function CounterModal({ offer, onClose, onDone }) {
  const [amount, setAmount] = useState(offer.current_amount || 100);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offers/${offer.offer_id}/counter`, { amount: Number(amount), message: message || null });
      toast.success("Counter offer sent", data?.credits_spent ? { description: `Counteroffer fee: ${data.credits_spent} credits.` } : undefined);
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not counter");
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">Negotiate Gracefully</div>
            <h3 className="font-serif text-2xl text-[#FBF8F3] mt-1">Counter Offer</h3>
          </div>
          <button onClick={onClose} className="text-[#9E978E] hover:text-[#FBF8F3]"><X size={22} /></button>
        </div>
        <div className="db-gold-rule my-5" />
        <div className="flex items-center justify-between rounded-xl bg-[#0C0A0A]/50 border border-[#D4AF37]/15 px-4 py-3 mb-4">
          <span className="text-sm text-[#9E978E]">Current Offer</span>
          <span className="font-serif text-xl text-[#FBF8F3]">${offer.current_amount}</span>
        </div>
        <div className="space-y-4">
          <Field label="Your Counter Amount (USD)">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-serif text-lg">$</span>
              <input data-testid="counter-offer-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls + " pl-8 font-serif text-lg"} />
            </div>
          </Field>
          <CounterFeeNote pricing={offer.counter_pricing} />
          <Field label="Message (optional)">
            <textarea data-testid="counter-offer-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="I'd prefer $150 — looking forward to meeting you." className={inputCls + " resize-none"} />
          </Field>
          <div className="flex gap-3 pt-1">
            <GhostButton onClick={onClose} className="flex-1">Cancel</GhostButton>
            <GoldButton data-testid="counter-offer-submit-btn" onClick={submit} disabled={busy} className="flex-1">{busy ? "Sending…" : (offer.counter_pricing?.next_counter_cost > 0 ? `Send Counter · ${offer.counter_pricing.next_counter_cost} credits` : "Send Counter")}</GoldButton>
          </div>
        </div>
      </div>
    </Overlay>
  );
}

function CounterFeeNote({ pricing }) {
  if (!pricing || pricing.free_allowance == null) return null;
  const cost = pricing.next_counter_cost;
  const left = Math.max(0, pricing.free_allowance - pricing.counters_used);
  return (
    <div data-testid="counter-fee-note" className="flex items-center justify-between rounded-xl bg-[#0C0A0A]/50 border border-[#D4AF37]/15 px-4 py-3 text-sm">
      <span className="flex items-center gap-1.5 text-[#D8D2C9]"><Coins size={14} className="text-[#D4AF37]" />
        {cost > 0 ? "Counteroffer fee" : `Free counteroffer (${left} of ${pricing.free_allowance} free left)`}</span>
      <span className="font-serif text-[#D4AF37]" data-testid="counter-fee-amount">{cost > 0 ? `${cost} credits` : "Free"}</span>
    </div>
  );
}
